import { profileById } from '@/data/catalog';
import { pickLocalized, t } from '@/i18n';
import { visibleOpinions } from '@/lib/opinions';
import type { Message, RoundType } from '@/types';
import type { ChatMessage, ContentPart } from '../types';

// Roughly 100k tokens of mixed text — under every current model's window with room for the reply.
export const DEFAULT_CHAR_BUDGET = 300_000;

/** Language-neutral marker the protocol keys on; the instruction text itself follows the UI language. */
export const DEBATE_MARKER = '[DEBATE]';

export function debateReplay(): string {
  return t('run.debateReplay');
}

export function debateInstruction(selfName: string): string {
  return t('run.debateInstruction', { name: selfName });
}

export interface AssembleOptions {
  messages: Message[];
  currentRound: number;
  roundType: RoundType;
  selfProfileId: string;
  loadImage: (id: string) => Promise<ContentPart | null>;
  charBudget?: number;
}

export interface Assembled {
  history: ChatMessage[];
  truncatedRounds: number;
}

/**
 * Builds the message list for one profile's call. Same-round isolation: only completed
 * rounds plus the current round's trigger are included — never the outputs other profiles
 * produced in the current round.
 */
export async function assembleHistory(o: AssembleOptions): Promise<Assembled> {
  const eligible = o.messages.filter((m) => m.round < o.currentRound || (m.round === o.currentRound && m.role === 'user'));
  const byRound = new Map<number, Message[]>();
  for (const m of [...eligible].sort((a, b) => a.seq - b.seq)) byRound.set(m.round, [...(byRound.get(m.round) ?? []), m]);

  const blocks: { round: number; chars: number; msgs: ChatMessage[] }[] = [];
  for (const [round, list] of [...byRound.entries()].sort(([a], [b]) => a - b)) {
    const msgs: ChatMessage[] = [];
    const user = list.find((m) => m.role === 'user');
    if (user) msgs.push({ role: 'user', content: await userContent(user, o.loadImage) });
    else if (list[0]?.roundType === 'debate') msgs.push({ role: 'user', content: [{ type: 'text', text: debateReplay() }] });

    const replies = list.filter((m) => m.role === 'profile' && m.status === 'complete' && (m.opinions?.length ?? 0) > 0);
    if (replies.length) msgs.push({ role: 'assistant', content: [{ type: 'text', text: replies.map(renderReply).join('\n\n') }] });

    if (msgs.length) blocks.push({ round, chars: countChars(msgs), msgs });
  }

  if (o.roundType === 'debate') {
    const self = profileById(o.selfProfileId);
    const text = debateInstruction(self ? pickLocalized(self.name) : o.selfProfileId);
    blocks.push({ round: o.currentRound, chars: text.length, msgs: [{ role: 'user', content: [{ type: 'text', text }] }] });
  }

  const budget = o.charBudget ?? DEFAULT_CHAR_BUDGET;
  let truncatedRounds = 0;
  while (blocks.length > 1 && blocks.reduce((sum, b) => sum + b.chars, 0) > budget) {
    blocks.shift();
    truncatedRounds++;
  }

  return { history: mergeConsecutive(blocks.flatMap((b) => b.msgs)), truncatedRounds };
}

async function userContent(m: Message, loadImage: AssembleOptions['loadImage']): Promise<ContentPart[]> {
  const parts: ContentPart[] = [{ type: 'text', text: m.text ?? '' }];
  for (const id of m.imageIds ?? []) {
    const part = await loadImage(id);
    if (part) parts.push(part);
  }
  return parts;
}

/** Full-text replay of every displayed opinion (statements and analyses). */
export function renderReply(m: Message): string {
  const meta = m.profileId ? profileById(m.profileId) : undefined;
  const name = m.profileId ? (meta ? pickLocalized(meta.name) : m.profileId) : t('run.expertFallback');
  const lines = visibleOpinions(m.opinions ?? []).map((o, i) => {
    const head = `${i + 1}. ${t('run.replayConfidence', { pct: Math.round(o.confidence * 100) })} ${o.statement}`;
    return o.analysis ? `${head}\n${o.analysis}` : head;
  });
  return `[${name}]\n${lines.join('\n\n')}`;
}

function countChars(msgs: ChatMessage[]): number {
  return msgs.reduce((sum, m) => sum + m.content.reduce((s, p) => s + (p.type === 'text' ? p.text.length : 4000), 0), 0);
}

/** Providers require strictly alternating roles; a round without replies would otherwise leave two user turns adjacent. */
function mergeConsecutive(history: ChatMessage[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  for (const m of history) {
    const prev = out[out.length - 1];
    if (prev && prev.role === m.role) {
      const gap: ContentPart = { type: 'text', text: '\n\n' };
      out[out.length - 1] = { role: prev.role, content: [...prev.content, gap, ...m.content] };
    } else {
      out.push({ role: m.role, content: [...m.content] });
    }
  }
  return out;
}
